/**
 * `api/useMemberInbox.ts` — the member app's Inbox reads + actions (#1666).
 *
 * **Degrades to a quiet empty state when the endpoints are absent.** This is
 * load-bearing, not defensive: the SPA auto-deploys and the backend does NOT
 * (the deploy-ordering discipline), so between the two deploys every request
 * here 404s. P0 set the precedent with the window-stamp fallback — absent data
 * means "nothing to show", never a crash and never an error banner blaming the
 * member. `inboxUnavailable` is exported so the tab can say the calm thing.
 *
 * Types are hand-typed mirrors rather than `schema.gen.ts` reads, per the
 * `useMeScheduleWindow` convention: the generated schema is the wire truth, the
 * mirror declares only what the caller reads — and a not-yet-deployed endpoint
 * has no generated entry to import on an older checkout.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useAuth } from "../lib/AuthContext";
import { ApiError, apiFetch } from "./client";

/** §7's N1..N10 keys. The three unbuilt ones ship in P2/P4. */
export type InboxCardType =
  | "roster_published"
  | "roster_updated"
  | "swap_ask"
  | "cover_ask"
  | "swap_outcome"
  | "leave_state"
  | "task_assigned"
  | "announcement"
  | "submission_window"
  | "feedback_prompt"
  // #2193 — a member's fixed-shift ask (lead's card) and its answer (hers).
  | "fixed_shift_ask"
  | "fixed_shift_outcome"
  // #2186 — a lead's card: changes for tomorrow are not published yet.
  | "changes_waiting"
  // #2629 — a lead's card: Tarii pinned a stand-in on a standing placement.
  | "auto_cover"
  // #2969 — a lead's card: a leave change after publish and its held re-solve.
  | "leave_change";

export type InboxStratum = "needs_you" | "updates";

export interface InboxCard {
  id: string;
  team_id: string;
  type: InboxCardType | string;
  stratum: InboxStratum | string;
  /** Structured card fields — the SPA composes the copy (§5.2.3). */
  payload: Record<string, unknown>;
  target_type: string | null;
  target_id: string | null;
  anchor_date: string | null;
  created_at: string;
  read_at: string | null;
  resolved_at: string | null;
  /** The archive's one-line why (§5.2.4); null on a hand-archived row. */
  resolution: string | null;
  archived_at: string | null;
}

export interface InboxResponse {
  needs_you: InboxCard[];
  updates: InboxCard[];
  needs_you_count: number;
}

export interface InboxArchiveResponse {
  items: InboxCard[];
  total: number;
}

export interface NotificationPrefsResponse {
  prefs: Record<string, Record<string, boolean>>;
  types: string[];
  channels: string[];
  /** {type: [channel]} the member may not change — roster truth (§7 rule 6). */
  locked: Record<string, string[]>;
  /**
   * #1923 — {type: [channel]} nothing is ever sent on (the lead's off-sick
   * email has no Inbox card). Optional: a backend that predates it sends none.
   */
  unavailable?: Record<string, string[]>;
}

const EMPTY_INBOX: InboxResponse = { needs_you: [], updates: [], needs_you_count: 0 };
const STALE_TIME_MS = 30_000;

export function memberInboxKey(accountId: string | null) {
  return ["member-inbox", accountId ?? ""] as const;
}

export function memberInboxArchiveKey(accountId: string | null, query: string) {
  return ["member-inbox-archive", accountId ?? "", query] as const;
}

export function memberNotificationPrefsKey(accountId: string | null) {
  return ["member-notification-prefs", accountId ?? ""] as const;
}

/**
 * True when the failure means "this backend doesn't have the Inbox yet".
 *
 * 404 is the only signal that matters: the router has no such path. A 403 or a
 * 500 is a real problem and must NOT be laundered into a calm empty state —
 * that would hide a genuine outage behind "you're all caught up".
 */
export function isInboxUnavailable(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export function useMemberInbox() {
  const { accountId } = useAuth();
  const query = useQuery<InboxResponse, ApiError | Error>({
    queryKey: memberInboxKey(accountId),
    enabled: Boolean(accountId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<InboxResponse>("/api/account/me/notifications"),
  });

  const unavailable = isInboxUnavailable(query.error);
  return {
    ...query,
    /** Always a usable shape — an absent backend reads as an empty inbox. */
    inbox: query.data ?? EMPTY_INBOX,
    inboxUnavailable: unavailable,
    /** A real failure the member should be told about (never a 404). */
    loadError: !unavailable && query.isError ? errorText(query.error) : null,
  };
}

/** The tab badge (D2): live Needs-you only, and 0 while the backend is absent. */
export function useInboxBadgeCount(): number {
  const { accountId } = useAuth();
  const query = useQuery<InboxResponse, ApiError | Error>({
    queryKey: memberInboxKey(accountId),
    enabled: Boolean(accountId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<InboxResponse>("/api/account/me/notifications"),
  });
  return query.data?.needs_you_count ?? 0;
}

export function useMemberInboxArchive(search: string) {
  const { accountId } = useAuth();
  const trimmed = search.trim();
  const query = useQuery<InboxArchiveResponse, ApiError | Error>({
    queryKey: memberInboxArchiveKey(accountId, trimmed),
    enabled: Boolean(accountId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () =>
      apiFetch<InboxArchiveResponse>(
        `/api/account/me/notifications/archive${trimmed ? `?q=${encodeURIComponent(trimmed)}` : ""}`,
      ),
  });
  return {
    ...query,
    archive: query.data ?? { items: [], total: 0 },
    archiveUnavailable: isInboxUnavailable(query.error),
  };
}

export type CardAction = "read" | "archive" | "dismiss";

/** What the tab needs to render an optimistic resolve honestly (#1683). */
export interface InboxCardActionApi {
  /** Archive / Skip a card. Collapses on `onMutate`, rolls back on `onError`. */
  resolve: (id: string, action: CardAction) => void;
  /** True while this row is optimistically resolved (§6.5's 200ms collapse). */
  isCollapsing: (id: string) => boolean;
  /** The last failure, in plain words. Cleared the moment a resolve retries. */
  failure: string | null;
  isPending: boolean;
}

/**
 * One mutation for the three per-card verbs.
 *
 * `archive` is a filing action; `dismiss` is Skip — an ANSWER that stamps a
 * resolution and lets the card linger with its why-line. They are deliberately
 * different endpoints because they mean different things (§7's N10 `Skip`).
 *
 * **Optimistic with rollback — the memberapp convention (#1683).** The row
 * collapses in `onMutate`, not before the call: a failed POST used to leave a
 * ghost-archived row that looked exactly like success, which is the worst
 * possible outcome for a surface whose whole promise is "your edit registered".
 * `onError` puts the row back and says why; `onSettled` re-reads both the live
 * inbox and the archive, so the card's real home is whatever the server says.
 *
 * The collapse set lives here rather than in the tab so the optimism and its
 * rollback are one object — a caller cannot half-adopt the convention.
 */
export function useInboxCardAction(): InboxCardActionApi {
  const queryClient = useQueryClient();
  const { accountId } = useAuth();
  const [collapsing, setCollapsing] = useState<ReadonlySet<string>>(EMPTY_COLLAPSING);
  const [failure, setFailure] = useState<string | null>(null);

  const mutation = useMutation<InboxCard, ApiError | Error, { id: string; action: CardAction }>({
    mutationFn: ({ id, action }) =>
      apiFetch<InboxCard>(`/api/account/me/notifications/${id}/${action}`, { method: "POST" }),
    onMutate: ({ id }) => {
      setFailure(null);
      setCollapsing((prev) => new Set(prev).add(id));
    },
    onError: (error, { id, action }) => {
      setCollapsing((prev) => withoutId(prev, id));
      setFailure(actionFailureText(action, error));
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["member-inbox"] }),
        queryClient.invalidateQueries({ queryKey: ["member-inbox-archive"] }),
      ]);
      // The refetch is the truth. A card the server really did file is gone
      // from the live inbox, so its collapse flag has nothing left to hold —
      // dropping it here is what keeps the set from growing without bound.
      const live = queryClient.getQueryData<InboxResponse>(memberInboxKey(accountId));
      const stillLive = new Set(
        [...(live?.needs_you ?? []), ...(live?.updates ?? [])].map((card) => card.id),
      );
      setCollapsing((prev) => {
        const next = new Set([...prev].filter((id) => stillLive.has(id)));
        return next.size === prev.size ? prev : next;
      });
    },
  });

  return {
    resolve: (id, action) => mutation.mutate({ id, action }),
    isCollapsing: (id) => collapsing.has(id),
    failure,
    isPending: mutation.isPending,
  };
}

const EMPTY_COLLAPSING: ReadonlySet<string> = new Set<string>();

function withoutId(ids: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(ids);
  next.delete(id);
  return next;
}

/**
 * The honest failure line. It names the verb that failed, quotes the server's
 * own reason, and states where the card is now — a member who just watched a
 * row slide away needs to be told it slid back.
 */
function actionFailureText(action: CardAction, error: unknown): string {
  const verb = action === "archive" ? "archive" : action === "dismiss" ? "skip" : "update";
  const raw = errorText(error, "something went wrong");
  const reason = raw.endsWith(".") ? raw : `${raw}.`;
  return `Couldn't ${verb} that card — ${reason} It's still in your inbox.`;
}

export function useNotificationPrefs() {
  const { accountId } = useAuth();
  const query = useQuery<NotificationPrefsResponse, ApiError | Error>({
    queryKey: memberNotificationPrefsKey(accountId),
    enabled: Boolean(accountId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<NotificationPrefsResponse>("/api/account/me/notification-prefs"),
  });
  return {
    ...query,
    prefsUnavailable: isInboxUnavailable(query.error),
  };
}

export function useSaveNotificationPrefs() {
  const queryClient = useQueryClient();
  return useMutation<
    NotificationPrefsResponse,
    ApiError | Error,
    Record<string, Record<string, boolean>>
  >({
    mutationFn: (prefs) =>
      apiFetch<NotificationPrefsResponse>("/api/account/me/notification-prefs", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ prefs }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["member-notification-prefs"] });
      queryClient.invalidateQueries({ queryKey: ["member-inbox"] });
    },
  });
}

function errorText(error: unknown, fallback = "Couldn't load your inbox."): string {
  if (error instanceof ApiError) return error.detail;
  if (error instanceof Error) return error.message;
  return fallback;
}
